'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  InputSearchAdvanced,
  InputSearchAdvancedSearchHistoryOption,
  InputSearchAdvancedSuggestionOption,
} from '@//components/InputSearchAdvanced';
import { SuggestionDropdown } from '@//containers/SuggestionDropdown';
import useFilterHistory from '@//data/filter-history/useFilterHistory';
import useRemoveFilterHistory from '@//data/filter-history/useRemoveFilterHistory';
import useVehicleSuggestions from '@//data/suggestions/useVehicleSuggestions';
import useDebouncedValue from '@//hooks/useDebouncedValue';
import useSuggestionFoldoutManager from '@//hooks/useSuggestionFoldoutManager';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import { mergeUrlSearchParams, sanitizeParams } from '@//utils/vehicles-search/utils';

interface SearchInputProps {
  rootUrl: string;
  searchPage: string;
  searchParams: URLSearchParams;
  isFetching: boolean;
}

const SearchInput = ({ rootUrl, searchPage, searchParams, isFetching }: SearchInputProps) => {
  const t = useTranslations('pages.search');
  const filterHistory = useFilterHistory();
  const removeFilterHistoryMutation = useRemoveFilterHistory();
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { control, register, getValues, setValue } = useForm({
    defaultValues: { q: '' },
  });

  const searchBarQ = useWatch({ control, name: 'q' });
  const debouncedQ = useDebouncedValue(searchBarQ, 150);

  const requestVehicleSuggestions = useVehicleSuggestions(debouncedQ);
  const vehicleSuggestions = requestVehicleSuggestions.data;
  const { suggestions = [], queryString } = vehicleSuggestions || {};

  const filteredFilterHistory = useMemo(
    () => (!searchBarQ ? filterHistory : []),
    [searchBarQ, filterHistory],
  );

  const {
    activeIndex,
    activeSection,
    activeItem,
    sections,
    showDropdown,
    onFocus,
    goToNext,
    goToPrev,
    resetSuggestions,
  } = useSuggestionFoldoutManager({
    geoLocationSuggestions: [],
    vehicleSuggestions: suggestions,
    articleSuggestions: [],
    filterHistory: filteredFilterHistory,
    searchQuery: searchBarQ,
    queryString,
    clickOutsideRef: inputRef,
  });

  const handleSuggestionClick = useCallback(
    (suggestion: any) => {
      const { mergedParams, containChanges } = mergeUrlSearchParams(searchParams, suggestion.facet);
      setValue('q', '');
      if (containChanges) updateUrl(mergedParams);
      resetSuggestions();
    },
    [searchParams, setValue, updateUrl, resetSuggestions],
  );

  const handleHistoryClick = useCallback(
    (historyItem: any) => {
      setValue('q', '');
      updateUrl(sanitizeParams(historyItem.query));
      resetSuggestions();
    },
    [updateUrl, setValue, resetSuggestions],
  );

  const onRemoveFilterHistory = useCallback(
    (id: string) => removeFilterHistoryMutation.mutate(id),
    [removeFilterHistoryMutation],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        sections.some((section) => section.items.length > 0)
      ) {
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          goToNext();
        } else {
          goToPrev();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeItem) {
          if (activeItem.type === 'vehicle') {
            handleSuggestionClick(activeItem.item);
          } else if (activeItem.type === 'history') {
            handleHistoryClick(activeItem.item);
          }
        } else {
          const nextParams = new URLSearchParams(searchParams);
          const currentQ = getValues('q');
          if (currentQ) {
            nextParams.set('q', currentQ);
            setValue('q', '');
          } else {
            nextParams.delete('q');
          }
          updateUrl(sanitizeParams(nextParams));
          resetSuggestions();
        }
      }
    },
    [
      sections,
      goToNext,
      goToPrev,
      activeItem,
      handleSuggestionClick,
      handleHistoryClick,
      getValues,
      searchParams,
      setValue,
      updateUrl,
      resetSuggestions,
    ],
  );

  const loadingSuggestions = useMemo(
    () => !!searchBarQ && (searchBarQ !== queryString || requestVehicleSuggestions.isLoading),
    [searchBarQ, queryString, requestVehicleSuggestions.isLoading],
  );

  return (
    <InputSearchAdvanced
      ref={inputRef}
      id="vehicle-search"
      label="Search"
      register={register('q')}
      autoComplete="off"
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      placeholder={t('searchBar.placeholder')}
      suggestionExpanded={showDropdown}
      suggestionListId="vehicle-search-suggestions-list"
      suggestionActiveOptionId={
        activeIndex > -1 ? `vehicle-search-suggestions-option-${activeIndex}` : undefined
      }
      loading={loadingSuggestions || isFetching}
    >
      {showDropdown && (
        <SuggestionDropdown
          id="vehicle-search"
          sections={sections}
          activeIndex={activeIndex}
          activeSection={activeSection}
          renderVehicle={(item: any, isActive) => (
            <InputSearchAdvancedSuggestionOption
              key={`vehicle-search-vehicle-${item.label}`}
              id={`vehicle-search-vehicle-${item.label}`}
              href={`${searchPage}?${item.facet}`}
              title={item.label}
              count={item.term_count}
              isActive={isActive}
              onClick={() => handleSuggestionClick(item)}
            >
              {item.label}
            </InputSearchAdvancedSuggestionOption>
          )}
          renderHistory={(item: any, isActive) => (
            <InputSearchAdvancedSearchHistoryOption
              key={item.id}
              label={item.label}
              title={item.label}
              href={`${rootUrl}?${item.query}`}
              isActive={isActive}
              onRemove={() => onRemoveFilterHistory(item.id)}
              onClick={() => handleHistoryClick(item)}
            />
          )}
        />
      )}
    </InputSearchAdvanced>
  );
};

export default SearchInput;
